import { z } from "zod";
import type { WidgetModule, WidgetRenderContext } from "@salestrak/ui";
import { DescriptionWidgetBody } from "#app/widgets/summary/description-widget-body";
import { useIssueWidgetContext } from "#app/widgets/issue/issue-widget-context";

export const issueDescriptionConfigSchema = z.looseObject({});
export type IssueDescriptionConfig = z.infer<typeof issueDescriptionConfigSchema>;

/**
 * The Project's free-text description — the same section body the Account and
 * Contact stacks mount, so a note written on any of the three reads and
 * behaves the same. A viewer without edit access gets no callback and so reads
 * plain prose.
 *
 * In create mode the issue has no id yet, so the editor keys off a constant —
 * one draft, never carried onto a second entity — and writes land on the
 * create draft through the same callback.
 */
function IssueDescriptionRender(_: WidgetRenderContext<IssueDescriptionConfig>) {
  const ctx = useIssueWidgetContext();
  const entityKey = ctx.issue?.issueId ?? "new-project";
  // Keyed per entity: a pending debounced edit flushes to the entity it was
  // typed on, and never re-seeds onto the next one.
  return (
    <DescriptionWidgetBody
      key={entityKey}
      entityKey={entityKey}
      onChange={ctx.onUpdateDescription}
      value={ctx.description ?? ""}
    />
  );
}

export const issueDescriptionDef: WidgetModule<IssueDescriptionConfig> = {
  type: "issue-description",
  title: "Description",
  icon: "align-left",
  span: { minW: 3, minH: 1, maxW: 12, maxH: 12, defaultW: 12, defaultH: 2 },
  configSchema: issueDescriptionConfigSchema,
  render: IssueDescriptionRender,
};
